import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { createFellowshipBetweenCurrentAndRouteUser, patchFellowshipBetweenCurrentAndRouteUser, getContents, getInstrumentsAvailable, getMusicGenres, getProjects, getUser, getUserFellowsWithUserId, getUserInstrumentWithUserNickname, 
         getUserMusicalGenresWithNickname, patchPrivacityConfig, 
         patchUserInstrumentWithNicknameAndInstrumentId, patchUserMusicalGenreWithNicknameAndMusicalGenreId,
         createUserInstrumentWithNicknameAndInstrumentId,
         createUserMusicalGenreWithNicknameAndInstrumentId} from "../../api/sessionAPI"
import { AuthHeaders, Content, Fellowship, Instrument, MusicGenre, Project, User, UserInstrument, UserMoreInfo, UserMusicalGenre, UserPrivacityConfig, UserPropertyConfig } from "../../#interfaces/interfaces";


interface PatchProfileData {
  authHeaders: AuthHeaders;
  userNickname: string;
  patchBody: UserMoreInfo;
}

export interface GetUserProjectsRequestData{
  authHeaders: AuthHeaders;
  urlParams: GetProjectsRouteParams;
}

export interface GetProjectsRouteParams{
  userNickname: string;
  page: number;
}

interface GetProfileData {
    authHeaders: AuthHeaders;
    userNickname: string;
}

interface GetUserContentsData {
  authHeaders: AuthHeaders;
  routeParams: GetContentsRouteParams;
}

export interface GetContentsRouteParams{
  userNickname?: string; 
  contentsPage: number;
}


interface PatchPrivacityConfigData {
  authHeaders: AuthHeaders;
  userNickname: string;
  patchBody: PatchPrivacityConfigBody;
}

export interface PatchPrivacityConfigBody {
  contents?: PropertyBody;
  projects?: PropertyBody;
}

interface PropertyBody {
  id?: number;
  userPrivacityId?: number;
}

export interface GetUserMusicalGenresUrlParams {
  userNickname: string;  
}

export interface GetUserMusicalGenresRequestParams{
  headers: AuthHeaders;
  urlParams: GetUserMusicalGenresUrlParams;
}

export interface PatchUserInstrumentUrlParams{
  userNickname: string;
}

export interface PatchUserInstrumentBodyData{
  instrumentId: number;
  selected: boolean;
}

export interface PatchUserInstrumentsRequestParams{
  headers: AuthHeaders;
  urlParams: PatchUserInstrumentUrlParams;
  requestBodyData: PatchUserInstrumentBodyData; 
}


export interface PatchUserMusicalGenreUrlParams {
  userNickname: string;
}

export interface PatchUserMusicalGenreBodyData {
  selected: boolean;
  musicGenreId: number;
}

export interface PatchMusicalGenresRequestParams{
  headers: AuthHeaders;
  urlParams: PatchUserMusicalGenreUrlParams;
  requestBodyData: PatchUserMusicalGenreBodyData;
}

export interface CreateUserFellowshipRequestBodyData{
  profileNickname?: string;
}

export interface CreateUserFellowshipRequestParams{
  authHeaders: AuthHeaders;
  requestBodyData: CreateUserFellowshipRequestBodyData; 
}

export interface PatchUserFellowshipRequestUrlParams{
  profileNickname?: string;
  currentUserId?: number;
}

export interface PatchBodyUserFellowship{
  active: boolean
}

export interface PatchUserFellowshipRequestParams{
  authHeaders: AuthHeaders;
  urlParams: PatchUserFellowshipRequestUrlParams; 
  patchBody: PatchBodyUserFellowship;
}

export interface GetUserInstrumentUrlParams{
  userNickname: string;
}

export interface GetUserInstrumentsRequestParams{
  headers: AuthHeaders;
  urlParams: GetUserInstrumentUrlParams;
}

interface GetMusicalGenresRequestParams {
  authHeaders: AuthHeaders;
}

export interface CreateUserInstrumentUrlParams {
  currentUserNickname: string;
}

export interface CreateUserInstrumentBodyData {
  userId: number;
  instrumentId: number;
}

export interface CreateUserInstrumentRequestParams{
  headers: AuthHeaders;
  urlParams: CreateUserInstrumentUrlParams;
  requestBodyData: CreateUserInstrumentBodyData;
}

export interface CreateUserMusicalGenreUrlParams {
  currentUserNickname: string;
}

export interface CreateUserMusicalGenreBodyData {
  userId: number;
  musicGenreId: number;
}

export interface CreateUserMusicalGenreRequestParams{
  headers: AuthHeaders;
  urlParams: CreateUserMusicalGenreUrlParams;
  requestBodyData: CreateUserMusicalGenreBodyData;
}

export interface GetCurrentUserFelowsUrlParams{
  currentUserId: number;
}

export interface GetCurrentUserFelowsRequestParams{
  authHeaders: AuthHeaders;
  urlParams: GetCurrentUserFelowsUrlParams;
}

export interface AddRemoveUserInstrument{
  userId: number;
  instrumentId: number;
  selected: boolean;
}

export interface AddRemoveUserMusicalGenre{
  userMusicalGenreId: number;
  selected: boolean;
}

interface ProfileData {
    user?: User;
    userMoreInfo: UserMoreInfo;
    userPrivacityConfiguration: UserPrivacityConfig;
    errors: [];
    userContents: Content[];
    userProjects: Project[];
    loading?: boolean;
    instrumentsAvailable: Instrument[];
    userInstruments: UserInstrument[];
    loadingProfilePage: boolean;
    musicalGenresAvailable: MusicGenre[];
    userMusicalGenres: UserMusicalGenre[];
    loadingMoreUserContents: boolean;
    loadingMoreUserProject: boolean;
    currentUserFellows: Fellowship[];
}

const initialState: ProfileData = {
    user: undefined,
    userMoreInfo: {  
      name: '',
      nickname: '',
      email: '',
      phoneNumber: '',
      website: '',
      lastName: '',
      city: '',
      state: '',
      country: '',
      about: '',
      avatarUrl: '',
      cover: undefined
    },
    userPrivacityConfiguration: {
      contents: {},
      projects: {}
    },
    errors: [],
    userContents: [],
    userProjects: [],
    loading: false,
    instrumentsAvailable: [],
    musicalGenresAvailable: [],
    currentUserFellows: [],
    userInstruments: [],
    userMusicalGenres: [],
    loadingMoreUserContents: false,
    loadingProfilePage: false,
    loadingMoreUserProject: false
}

export const getGenericUserInfo = createAsyncThunk(
    'sessionProfile/getGenericUserInfo',
    async (payload: GetProfileData, {rejectWithValue}) => {
        const response = await getUser(
            payload.authHeaders,
            payload.userNickname,
        )
        if (response.status >= 200 && response.status < 300) {
            return response.data 
        } else {
          return rejectWithValue(response.data)
        }
    }
)

export const getUserContents = createAsyncThunk(
  'sessionProfile/getUserContents',
  async (payload: GetUserContentsData, {rejectWithValue}) => {
      const response = await getContents(
          payload.authHeaders,
          payload.routeParams
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const getRouteProjects = createAsyncThunk(
  'sessionProfile/getRouteProjects',
  async (payload: GetUserProjectsRequestData, {rejectWithValue}) => {
      const response = await getProjects(
          payload.authHeaders,
          payload.urlParams
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const getUserMusicalGenres = createAsyncThunk(
  'sessionProfile/getUserMusicalGenresWithNickname',
  async (payload: GetUserMusicalGenresRequestParams, {rejectWithValue}) => {
      const response = await getUserMusicalGenresWithNickname(
          payload.headers,
          payload.urlParams
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const getInstruments = createAsyncThunk(
  'sessionProfile/getInstruments',
  async (_, { rejectWithValue }) => {
      const response = await getInstrumentsAvailable()

      if (response.status >= 200 && response.status<300){
        return response.data
      } else {
        return rejectWithValue(response)
      }
  }
)

export const getUserInstruments = createAsyncThunk(
  'sessionProfile/getUserInstruments',
  async (payload: GetUserInstrumentsRequestParams, {rejectWithValue}) => {
      const response = await getUserInstrumentWithUserNickname(
          payload.headers,
          payload.urlParams
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const getUserInstrumentsProfile = createAsyncThunk(
  'sessionProfile/getUserInstrumentsProfile',
  async (payload: GetUserInstrumentsRequestParams, {rejectWithValue}) => {
      const response = await getUserInstrumentWithUserNickname(
          payload.headers,
          payload.urlParams
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const getMusicalGenresAvailable = createAsyncThunk(
  'groupsSlice/getMusicalGenresAvailable',
  async (payload: GetMusicalGenresRequestParams, {rejectWithValue}) => {
      const response = await getMusicGenres(
          payload.authHeaders
      )

      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const createUserMusicalGenre = createAsyncThunk(
  'sessionProfile/createUserMusicalGenre',
  async (payload: CreateUserMusicalGenreRequestParams, {rejectWithValue}) => {
      const response = await createUserMusicalGenreWithNicknameAndInstrumentId(
          payload.headers,
          payload.urlParams,
          payload.requestBodyData
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)


export const patchUserMusicalGenre = createAsyncThunk(
  'groupsSlice/patchUserMusicalGenre',
  async (payload: PatchMusicalGenresRequestParams, {rejectWithValue}) => {
      const response = await patchUserMusicalGenreWithNicknameAndMusicalGenreId(
          payload.headers,
          payload.urlParams,
          payload.requestBodyData
      )

      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const createUserInstrument = createAsyncThunk(
  'sessionProfile/createUserInstrument',
  async (payload: CreateUserInstrumentRequestParams, {rejectWithValue}) => {
      const response = await createUserInstrumentWithNicknameAndInstrumentId(
          payload.headers,
          payload.urlParams,
          payload.requestBodyData
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const patchUserInstrument = createAsyncThunk(
  'sessionProfile/patchUserInstrument',
  async (payload: PatchUserInstrumentsRequestParams, {rejectWithValue}) => {
      const response = await patchUserInstrumentWithNicknameAndInstrumentId(
          payload.headers,
          payload.urlParams,
          payload.requestBodyData
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const getCurrentUserFelows = createAsyncThunk(
  'sessionProfile/getCurrentUserFelows',
  async (payload: GetCurrentUserFelowsRequestParams, {rejectWithValue}) => {
      const response = await getUserFellowsWithUserId(
          payload.authHeaders,
          payload.urlParams
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const createUserFellowship = createAsyncThunk(
  'sessionProfile/createUserFellowship',
  async (payload: CreateUserFellowshipRequestParams, {rejectWithValue}) => {
      const response = await createFellowshipBetweenCurrentAndRouteUser(
          payload.authHeaders,
          payload.requestBodyData
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const patchUserFellowship = createAsyncThunk(
  'sessionProfile/patchUserFellowship',
  async (payload: PatchUserFellowshipRequestParams, {rejectWithValue}) => {
      const response = await patchFellowshipBetweenCurrentAndRouteUser(
          payload.authHeaders,
          payload.urlParams,
          payload.patchBody
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

const profileSlice = createSlice({
    name: 'sessionProfile',
    initialState,
    reducers: {
      selectUserInstrument: (state, action: PayloadAction<AddRemoveUserInstrument>) => {
        const { userId, instrumentId } = action.payload;
      
        const foundInstrument = state.instrumentsAvailable.find(instrument => instrument.id === instrumentId);
        const userInstrumentSelected = state.userInstruments.find(
          userInstrument => userInstrument.instrument.id === instrumentId && userInstrument.selected === true
        );
      
        if (foundInstrument && !userInstrumentSelected) {
          state.userInstruments.push({ userId: userId, instrumentId: instrumentId, selected: true, instrument: foundInstrument });
        } else if (foundInstrument && userInstrumentSelected) {
          const userInstrumentIndex = state.userInstruments.findIndex(
            userInstrument => userInstrument.instrument.id === foundInstrument.id
          );
          state.userInstruments[userInstrumentIndex].selected = !state.userInstruments[userInstrumentIndex].selected;
        }
      },
      deselectUserInstrument: (state, action: PayloadAction<AddRemoveUserInstrument>) => {
        const { instrumentId } = action.payload;
      
        const foundInstrument = state.instrumentsAvailable.find(instrument => instrument.id === instrumentId);
        const userInstrumentSelected = state.userInstruments.find(
          userInstrument => userInstrument.instrument.id === instrumentId && userInstrument.selected === true
        );
      
        if (foundInstrument && userInstrumentSelected) {
          const userInstrumentIndex = state.userInstruments.findIndex(
            userInstrument => userInstrument.instrument.id === foundInstrument.id
          );
          state.userInstruments[userInstrumentIndex].selected = !state.userInstruments[userInstrumentIndex].selected;
        }
      },
      addRemoveUserMusicalGenre: (state, action: PayloadAction<AddRemoveUserMusicalGenre>) => {
        const { userMusicalGenreId, selected } = action.payload;
          const existingMusicGenreIndex = state.userMusicalGenres.findIndex(
            (userMusicalGenre) => userMusicalGenre.id === userMusicalGenreId
        );
    
        if (existingMusicGenreIndex !== -1) {
            state.userMusicalGenres[existingMusicGenreIndex].selected = selected;
        }
      }
    },
    extraReducers: (builder) => {
      builder
      .addCase(getGenericUserInfo.pending, (state) => {
        state.loadingProfilePage = true
      })
      .addCase(getGenericUserInfo.fulfilled, (state, action: any) => {
        const responseCamelCase = convertKeysToCamelCaseAndSortByUpdatedAt(action.payload);
        const responseMoreInfo = responseCamelCase.userMoreInfo;
        const responsePrivacityConfiguration = responseCamelCase.userPrivacityConfiguration;
        const contents = responsePrivacityConfiguration.find((obj: UserPropertyConfig) => obj.userPropertyTypeId === 1);
        const projects = responsePrivacityConfiguration.find((obj: UserPropertyConfig) => obj.userPropertyTypeId === 2);
        
        state.userMoreInfo = responseMoreInfo;
        state.userPrivacityConfiguration.contents = contents;
        state.userPrivacityConfiguration.projects = projects;
        state.loadingProfilePage = false;
      })
      .addCase(getGenericUserInfo.rejected, (state) => {
        state.loadingProfilePage = false
      })
      .addCase(getUserContents.pending, (state, action: any) => {

        if (action.meta.arg.routeParams.contentsPage === 1){
          state.userContents = [];
        }

        state.loadingMoreUserContents = true;
      })
      .addCase(getUserContents.fulfilled, (state, action: any) => {
        
        state.userContents = [...state.userContents,...convertKeysToCamelCaseAndSortByUpdatedAt(action.payload)]

        state.loadingMoreUserContents = false;
      })
      .addCase(getUserContents.rejected, (state) => {
        state.loadingMoreUserContents = false;
      })
      .addCase(getRouteProjects.pending, (state, action: any) => {
        if (action.meta.arg.urlParams.page === 1) {
          state.userProjects = [];
          state.loadingProfilePage = true;
        }
        state.loadingMoreUserProject = true;
      })
      .addCase(getRouteProjects.fulfilled, (state, action: any) => {
        const allUserProjects = convertKeysToCamelCase(action.payload)
        const publicUserProjects = allUserProjects.userRoutePublicPrivate[0]
        const privateUserProjects = allUserProjects.userRoutePublicPrivate[1]
        const publicProjectsUserAsContributor = allUserProjects.userRouteContributorPublicPrivate[0]
        const privateProjectsUserAsContributor = allUserProjects.userRouteContributorPublicPrivate[1]
        const newProjects = sortAndMergeGroups(publicUserProjects,
                                               privateUserProjects,
                                               publicProjectsUserAsContributor,
                                               privateProjectsUserAsContributor)
        if (action.meta.arg.urlParams.page === 1) {
          state.userProjects = newProjects;
        } else {
          state.userProjects = [...state.userProjects, ...newProjects];
        }
        state.loadingProfilePage = false;
        state.loadingMoreUserProject = false;
      })
      .addCase(getRouteProjects.rejected, (state) => {
        state.loadingProfilePage = false;
        state.loadingMoreUserProject = false;
      })
      .addCase(getInstruments.pending, (state) => {
        state.instrumentsAvailable = [];
      })
      .addCase(getInstruments.fulfilled, (state, action: any) => {
        
        state.instrumentsAvailable = convertKeysToCamelCase(action.payload);
      })
      .addCase(getInstruments.rejected, (state) => {
        state.instrumentsAvailable = [];
      })
      .addCase(getUserInstrumentsProfile.pending, (state) => {
        state.loadingProfilePage = true;
      })
      .addCase(getUserInstrumentsProfile.fulfilled, (state, action: any) => {
        const filteredInstruments = convertKeysToCamelCase(action.payload).filter(
          (userInstrument: UserInstrument) => userInstrument.selected === true
        );

        state.userInstruments = filteredInstruments;
        state.loadingProfilePage = false;
      })
      .addCase(getUserInstrumentsProfile.rejected, (state) => {
        state.loadingProfilePage = false;
      })
      .addCase(getUserInstruments.pending, (state) => {
        state.userInstruments = [];
        state.loadingProfilePage = true;
      })
      .addCase(getUserInstruments.fulfilled, (state, action: any) => {
        state.userInstruments = convertKeysToCamelCase(action.payload);
        state.loadingProfilePage = false;
      })
      .addCase(getUserInstruments.rejected, (state) => {
        state.userInstruments = [];
        state.loadingProfilePage = false;
      })
      .addCase(createUserInstrument.pending, (state, action) => {
        const currentUserId = action.meta.arg.requestBodyData.userId;
        const instrumentIdSelected = action.meta.arg.requestBodyData.instrumentId; 
        const instrumentAvailableSelected = state.instrumentsAvailable.find(instrument => instrument.id === instrumentIdSelected)
        
        if (currentUserId && instrumentIdSelected && instrumentAvailableSelected){
          state.userInstruments.push({userId: currentUserId, 
                                      instrumentId: instrumentIdSelected, 
                                      instrument: instrumentAvailableSelected, 
                                      selected: true})
        }
        state.loading = false;
      })
      .addCase(createUserInstrument.fulfilled, (state, action: any) => {
        state.loading = false;
      })
      .addCase(createUserInstrument.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createUserMusicalGenre.pending, (state, action) => {
        const currentUserId = action.meta.arg.requestBodyData.userId;
        const musicGenreIdSelected = action.meta.arg.requestBodyData.musicGenreId; 
        const musicGenreAvailableSelected = state.musicalGenresAvailable.find(musicGenre => musicGenre.id === musicGenreIdSelected)
        
        if (currentUserId && musicGenreIdSelected && musicGenreAvailableSelected){
          state.userMusicalGenres.push({userId: currentUserId, 
                                      musicGenreId: musicGenreIdSelected, 
                                      musicGenre: musicGenreAvailableSelected, 
                                      selected: true})
        }
        state.loading = false;
      })
      .addCase(createUserMusicalGenre.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createUserMusicalGenre.rejected, (state) => {
        state.loading = false;
      })
      .addCase(patchUserInstrument.pending, (state, action) => {
        const requestParamsCamelCase = convertKeysToCamelCase(action);

        const userInstrumentIndex = state.userInstruments.findIndex(userInstrument => userInstrument.instrumentId === requestParamsCamelCase.meta.arg.requestBodyData.instrumentId)
        state.userInstruments[userInstrumentIndex].selected = requestParamsCamelCase.meta.arg.requestBodyData.selected;
        state.loading = false;
      })
      .addCase(patchUserInstrument.fulfilled, (state, action: any) => {
        state.loading = false;
      })
      .addCase(patchUserInstrument.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getMusicalGenresAvailable.pending, (state) => {
        state.loading = false;
      })
      .addCase(getMusicalGenresAvailable.fulfilled, (state, action: any) => {
        state.musicalGenresAvailable = convertKeysToCamelCase(action.payload)
        state.loading = false;
      })
      .addCase(getMusicalGenresAvailable.rejected, (state) => {
        state.loading = false;
      })
      .addCase(patchUserMusicalGenre.pending, (state, action: any) => {
        const requestParamsCamelCase = convertKeysToCamelCase(action);

        const userMusicalGenreIndex = state.userMusicalGenres.findIndex(userMusicalGenre => userMusicalGenre.musicGenreId === requestParamsCamelCase.meta.arg.requestBodyData.musicGenreId)
        state.userMusicalGenres[userMusicalGenreIndex].selected = requestParamsCamelCase.meta.arg.requestBodyData.selected;
        state.loading = false;
      })
      .addCase(patchUserMusicalGenre.fulfilled, (state, action: any) => {
        state.loading = false;
      })
      .addCase(patchUserMusicalGenre.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getUserMusicalGenres.pending, (state) => {
        state.userMusicalGenres = [];
        state.loadingProfilePage = true;
      })
      .addCase(getUserMusicalGenres.fulfilled, (state, action: any) => {
        state.userMusicalGenres = convertKeysToCamelCase(action.payload);
        state.loadingProfilePage = false;
      })
      .addCase(getUserMusicalGenres.rejected, (state) => {
        state.userMusicalGenres = [];
        state.loadingProfilePage = false;
      })
      .addCase(createUserFellowship.pending, (state) => {
        state.loading = false;
      })
      .addCase(createUserFellowship.fulfilled, (state, action: any) => {
        state.currentUserFellows = [...state.currentUserFellows,convertKeysToCamelCase(action.payload)];
        state.loading = false;
      })
      .addCase(createUserFellowship.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getCurrentUserFelows.pending, (state) => {
        state.loadingProfilePage = true;
      })
      .addCase(getCurrentUserFelows.fulfilled, (state, action: any) => {
        state.currentUserFellows = convertKeysToCamelCase(action.payload);
        state.loadingProfilePage = false;
      })
      .addCase(getCurrentUserFelows.rejected, (state) => {
        state.loadingProfilePage = false;
      })
      .addCase(patchUserFellowship.pending, (state) => {
        state.loading = false;
      })
      .addCase(patchUserFellowship.fulfilled, (state, action: any) => {
        state.currentUserFellows = state.currentUserFellows.map(fellowship =>
          fellowship.id === action.payload.id
              ? { ...fellowship, ...convertKeysToCamelCase(action.payload) }
              : fellowship
      );
        state.loading = false;
      })
      .addCase(patchUserFellowship.rejected, (state) => {
        state.loading = false;
      })
      }
    })

export const { selectUserInstrument, deselectUserInstrument, addRemoveUserMusicalGenre } = profileSlice.actions;

export const profileSliceReducers = profileSlice.reducer
export const profileSliceActions = profileSlice.actions

export function sortAndMergeGroups(...lists: any[][]): any[] {

  const mergedList = ([] as any[]).concat(...lists);

  const compareDates = (a: string, b: string) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return  dateB.getTime() - dateA.getTime();
  };

  mergedList.sort((a, b) => compareDates(a.created_at, b.created_at));

  const mergedListConverted = convertKeysToCamelCase(mergedList);
  return mergedListConverted;
}

export function convertKeysToCamelCaseAndSortByUpdatedAt(obj: any): any {
  function convertKeysToCamelCase(inputObj: any): any {
    if (typeof inputObj === 'object' && inputObj !== null) {
      if (Array.isArray(inputObj)) {
        return inputObj.map(item => convertKeysToCamelCase(item));
      } else {
        if (inputObj.constructor === Object) {
          const newObj: { [key: string]: any } = {};
          for (const key in inputObj) {
            if (Object.prototype.hasOwnProperty.call(inputObj, key)) {
              const camelCaseKey = key.replace(/_([a-z])/g, (match, letter) =>
                letter.toUpperCase()
              );
              newObj[camelCaseKey] = convertKeysToCamelCase(inputObj[key]);
            }
          }
          return newObj;
        }
      }
    }
    return inputObj;
  }

  const camelCasedObj = convertKeysToCamelCase(obj);

  if (Array.isArray(camelCasedObj)) {
    return camelCasedObj.sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB.getTime() - dateA.getTime();
    });
  } else {
    return camelCasedObj;
  }
}

export function convertKeysToCamelCase(obj: any): any {
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      return obj.map(item => convertKeysToCamelCase(item));
    } else if (obj.constructor === Object) {
      const newObj: { [key: string]: any } = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const camelCaseKey = key.replace(/_([a-z])/g, (_, letter) =>
            letter.toUpperCase()
          );
          newObj[camelCaseKey] = convertKeysToCamelCase(obj[key]);
        }
      }
      return newObj;
    }
  }
  return obj;
}

function convertKeysToSnakeCase(obj: any): any {
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      return obj.map(item => convertKeysToSnakeCase(item));
    } else {
      if (obj.constructor === Object) {
        const newObj: { [key: string]: any } = {};
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const snakeCaseKey = key === 'accessToken' ? key : key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`);
            newObj[snakeCaseKey] = obj[key];
          }
        }
        return newObj;
      }
    }
  }
  return obj;
}

export function convertKeysToCamelCaseAndSortByName(objList: any[]): any[] {
  function convertKeysToCamelCase(inputObj: any): any {
    if (typeof inputObj === 'object' && inputObj !== null) {
      if (Array.isArray(inputObj)) {
        return inputObj.map(item => convertKeysToCamelCase(item));
      } else {
        if (inputObj.constructor === Object) {
          const newObj: { [key: string]: any } = {};
          for (const key in inputObj) {
            if (Object.prototype.hasOwnProperty.call(inputObj, key)) {
              const camelCaseKey = key.replace(/_([a-z])/g, (match, letter) =>
                letter.toUpperCase()
              );
              newObj[camelCaseKey] = convertKeysToCamelCase(inputObj[key]);
            }
          }
          return newObj;
        }
      }
    }
    return inputObj;
  }

  return objList
    .map(convertKeysToCamelCase)
    .sort((a, b) => a.name.localeCompare(b.name));
}